// stores/formStore.js
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { formDataReducer } from '../reducers/formData';
import { DEFAULT_FORM_DATA } from '../constants';

export const useFormStore = create(
  persist(
    (set, get) => ({
      // Your form data
      formData: DEFAULT_FORM_DATA,
      
      // Partner processing tracking 
      processedPartnerData: new Set(),
      
      // Core dispatch method using your existing reducer
      dispatch: (action) => {
        const currentState = get().formData;
        const newState = formDataReducer(currentState, action);
        set({ formData: newState });
      },
      
      // Partner data tracking methods
      markPartnerProcessed: (partnerId, cruiseLineId) => {
        const key = `${partnerId}-${cruiseLineId}`;
        set(state => ({
          processedPartnerData: new Set([...state.processedPartnerData, key])
        }));
      },
      
      hasProcessedPartner: (partnerId, cruiseLineId) => {
        const key = `${partnerId}-${cruiseLineId}`;
        return get().processedPartnerData.has(key);
      },
      
      clearProcessedPartners: () => {
        set({ processedPartnerData: new Set() });
      },

      // Action creators
      actions: {
        leg: {
          updateField: (legIndex, field, value) => 
            get().dispatch({ type: 'UPDATE_LEG_FIELD', legIndex, field, value }),
          
          addProduct: (legIndex, product) =>
            get().dispatch({ type: 'ADD_PRODUCT', legIndex, product }),
          
          removeProduct: (legIndex, productId) =>
            get().dispatch({ type: 'REMOVE_PRODUCT', legIndex, productId }),

          updateProductField: (legIndex, productId, field, value) =>
            get().dispatch({ type: 'UPDATE_PRODUCT_FIELD', legIndex, productId, field, value }),
          
          updateFields: (legIndex, updates) =>
            get().dispatch({ type: 'BATCH_LEG_UPDATES', legIndex, updates }),
          
          updateBookingData: (from, to, pricing) =>
            get().dispatch({ type: 'UPDATE_BOOKING_DATA', from, to, pricing })
        },
        
        cruise: {
          setData: (data) => 
            get().dispatch({ type: 'SET_CRUISE_DATA', data }),
          
          updateCruiseData: (id, data) =>
            get().dispatch({ type: 'UPDATE_CRUISE_DATA', id, data }),
          
          setCurrentLine: (cruiseLine) =>
            get().dispatch({ type: 'SET_CURRENT_CRUISE_LINE', cruiseLine }),
          
          updateItineraryFilter: (field, label) =>
            get().dispatch({ type: 'UPDATE_ITINERARY_FILTER', field, label })
        },
        
        customer: {
          update: (updates) =>
            get().dispatch({ type: 'UPDATE_CUSTOMER_INFO', updates })
        },
        
        payment: {
          update: (updates) =>
            get().dispatch({ type: 'UPDATE_PAYMENT_INFO', updates })
        },
        
        agent: {
          setAgentId: (agentId) =>
            get().dispatch({ type: 'SET_AGENT_ID', agentId }),
          setAdvisorData: (data) =>
            get().dispatch({ type: 'SET_ADVISOR_DATA', data })
        },

        form: {
          reset: () => get().dispatch({ type: 'RESET_FORM' }),
          setCurrentPage: (index, value) => get().dispatch({ type: 'SET_CURRENT_PAGE', index, value }),
          updateField: (field, value) => get().dispatch({ type: 'UPDATE_FORM_FIELD', field, value }),
          updateFields: (updates) => get().dispatch({ type: 'UPDATE_FORM_FIELDS', updates }),
          addLeg: (leg) => get().dispatch({ type: 'ADD_LEG', leg }),
          removeLeg: (legIndex) => get().dispatch({ type: 'REMOVE_LEG', legIndex }),
          setData: (data) => {
            if (typeof data === 'function') {
              get().dispatch({ type: 'SET_FORM_DATA_FUNCTION', updater: data });
            } else {
              get().dispatch({ type: 'SET_FORM_DATA', data });
            }
          }
        }
      },

      // Helper functions (exactly matching your existing ones)
      helpers: {
        getAuthToken: () => get().formData.authToken || '',
        getBrandId: () => get().formData.brandId || '',
        getLegs: () => get().formData.legs || [],
        getLeg: (legIndex) => get().formData.legs?.[legIndex] || null,
        addLeg: () => {
          const state = get();
          const legs = state.formData.legs || [];
          const lastLeg = legs[legs.length - 1] || {};
          const newLegIndex = legs.length;
          const newLeg = {
            id: `leg-${newLegIndex}`,
            legIndex: newLegIndex,
            fromAddress: {},
            toAddress: {},
            availablePorts: [],
            cruiseSelection: { pickup: false, delivery: false },
            tripType: newLegIndex > 0 ? { id: 'round-trip', label: 'Round Trip' } : { id: 'one-way', label: 'One Way' },
            // if we have no addresses we can't have pricing
            pricing: lastLeg.pricing || null,
            selectedProducts: lastLeg.selectedProducts ? [...lastLeg.selectedProducts] : null,
          };
          state.actions.form.addLeg(newLeg);
        },
        removeLeg: (legIndex) => {
          get().actions.form.removeLeg(legIndex);
        },
        getLegField: (legIndex, field) => get().formData.legs?.[legIndex]?.[field] || null,
        hasValidLegs: () => (get().formData.legs?.length || 0) > 0,
        isLegComplete: (legIndex) => {
          const leg = get().formData.legs?.[legIndex];
          return leg?.fromAddress && leg?.toAddress && leg?.selectedProducts?.length > 0;
        },
        getTotalLegs: () => get().formData.legs?.length || 0,
        hasLegField: (legIndex, field) => {
          const leg = get().formData.legs?.[legIndex];
          return !!leg?.[field];
        },
        getCruiseData: () => get().formData.cruiseData || [],
        getCustomerInfo: () => get().formData.customerInfo || {},
        getTraveler: () => get().formData.traveler || {},
        getCruiseLineData: (id) => {
          const cruiseData = get().formData.cruiseData || [];
          return cruiseData.find(cruise => cruise.id === id) || null;
        },
        hasCruisePickup: (index) => {
          const leg = get().formData.legs?.[index];
          return leg?.cruiseSelection?.pickup === true;
        },
        hasCruiseDelivery: (index) => {
          const leg = get().formData.legs?.[index];
          return leg?.cruiseSelection?.delivery === true;
        },
        getCruiseSelection: (index, type) => {
          const leg = get().formData.legs?.[index];
          return leg?.cruiseSelection?.[type] || false;
        },
        hasSelectedProducts: (index) => {
          const leg = get().formData.legs?.[index];
          return leg?.selectedProducts?.length > 0;
        },
        getOrderTotal: () => {
          const state = get();
          return state.formData.legs?.reduce((total, leg) => {
            const legTotal = state.helpers.getLegTotal(leg.legIndex);
            return total + legTotal;
          }, 0) || 0;
        },
        getLegTotal: (legIndex) => {
          const leg = get().formData.legs?.[legIndex];
          if (!leg) return 0;

          if (leg.selectedService) {
            return (leg.selectedService.combined_adjusted_price_cents || 
                   leg.selectedService.combined_price_cents || 0)
                   + (leg.selectedPickupFee?.price_cents || 0);
          } else {
            const selectedProducts = leg.selectedProducts || [];
            const totalPrice = selectedProducts.reduce((sum, product) => {
              const productPrice = product.price_cents || 0;
              return sum + productPrice * product.quantity + (leg.selectedPickupFee?.price_cents || 0);
            }, 0);
            return totalPrice;
          }
        },
        checkLegAddressComplete: (legIndex, type) => {
          const leg = get().formData.legs?.[legIndex];
          if (!leg) return {};
          const missing = {}
          const field = type === 'from' ? 'fromAddress' : 'toAddress';
          if(!leg?.[field]) // no address at all
            missing[`leg${legIndex}-${type}-address`] = `missing-${type}-address`;
          else {
            // Check if street address is complete. If there is a port 
            // we will use the agent address so no street is needed
            if(!leg?.[field]?.street && !leg?.[field]?.cruisePort?.id) {
              missing[`leg${legIndex}-${type}-address`] = `missing-${type}-address-data`;
            }
          }
          return missing;
        },
        checkLegProducts: (legIndex) => {
          // Check if leg has selected products
          const leg = get().formData.legs?.[legIndex];
          if (!leg) return {};
          const missing = {};
          if (!leg?.selectedProducts || leg.selectedProducts.length === 0) {
            missing[`leg${legIndex}-products`] = 'no-products-selected';
          }
          return missing;
        },
        checkLegServices: (legIndex) => {
          // check if leg has required services selected
          const leg = get().formData.legs?.[legIndex];
          if (!leg) return {};
          const missing = {};
          if (!leg?.selectedService || !leg.selectedService?.id) {
            missing[`leg${legIndex}-services`] = 'no-services-selected';
          }
          return missing;
        },
        checkLegDates: (legIndex) => {
          const leg = get().formData.legs?.[legIndex];
          if (!leg) return {};
          // we should have a ship date and delivery date since we selected
          // services
          const missing = {};
          if (!leg?.shipDate) {
            missing[`leg${legIndex}-ship-date`] = 'missing-ship-date';
          }
          if (!leg?.deliveryDate) {
            missing[`leg${legIndex}-delivery-date`] = 'missing-delivery-date';
          }
          if (leg?.cruiseSelection?.pickup && !leg?.disembarkDate) {
            missing[`leg${legIndex}-disembark-date`] = 'missing-disembark-date';
          }
          if (leg?.cruiseSelection?.delivery && !leg?.embarkDate) {
            missing[`leg${legIndex}-embark-date`] = 'missing-embark-date';
          }
          return missing;
        },
        checkLegComplete: (legIndex) => {
          const state = get();
          return{
            ...(state.helpers.checkLegAddressComplete(legIndex, 'from')),
            ...(state.helpers.checkLegAddressComplete(legIndex, 'to')),
            ...(state.helpers.checkLegProducts(legIndex)),
            ...(state.helpers.checkLegServices(legIndex)),
            ...(state.helpers.checkLegDates(legIndex))
          }
        },
        checkLegsComplete: () => {
          const state = get();
          const legs = state.formData.legs || [];
          let missing = {}
          legs.forEach((leg, index) => {
            const legMissing = state.helpers.checkLegComplete(index);
            missing = { ...missing, ...legMissing };
          });
          return missing;
        },
      }
    }),
    {
      name: 'shipping-form-storage', // localStorage key
      partialize: (state) => ({ 
        formData: state.formData,
        // don't persist the Set directly
        // processedPartnerData: Array.from(state.processedPartnerData) // Convert Set to Array for JSON
      }),
      // Rehydrate Set from Array
      merge: (persistedState, currentState) => ({
        ...currentState,
        ...persistedState,
        processedPartnerData: new Set(persistedState.processedPartnerData || [])
      })
    }
  )
);